import ApiHelper from '@/services/ApiHelper';

export interface CvvFeatureConfig {
    enabled: boolean;
    storeIds: string[];
}

const CVV_CONFIG_DISABLED: CvvFeatureConfig = { enabled: false, storeIds: [] };

let cvvConfig: CvvFeatureConfig | null = null;
let cvvConfigRequest: Promise<CvvFeatureConfig> | null = null;

const toBool = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    return ['1', 'true', 'yes', 'on'].includes(
        String(value ?? '')
            .trim()
            .toLowerCase()
    );
};

const toStoreIds = (value: unknown): string[] =>
    String(value ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

export const fetchCvvFeatureConfig = async (): Promise<CvvFeatureConfig> => {
    const response = await ApiHelper.get('/feature/cvv');
    return {
        enabled: toBool(response?.cvv_enabled),
        storeIds: toStoreIds(response?.cvv_store_ids),
    };
};

export const getCvvFeatureConfig = async (): Promise<CvvFeatureConfig> => {
    if (cvvConfig) return cvvConfig;
    if (!cvvConfigRequest) {
        cvvConfigRequest = fetchCvvFeatureConfig()
            .then((config) => {
                cvvConfig = config;
                return config;
            })
            .catch((error) => {
                console.log('CVV Feature Config Error', error ? ` - ${error}` : '');
                return CVV_CONFIG_DISABLED;
            })
            .finally(() => {
                cvvConfigRequest = null;
            });
    }
    return cvvConfigRequest;
};

export const isCvvRequiredForStore = async (storeId?: string): Promise<boolean> => {
    if (!storeId) return false;
    const config = await getCvvFeatureConfig();
    return config.enabled && config.storeIds.includes(String(storeId));
};

export const clearCvvFeatureConfigCache = () => {
    cvvConfig = null;
};

export interface BrazeFeatureConfig {
    ecommerceEnabled: boolean;
    guestCapture: boolean;
}

const BRAZE_CONFIG_DISABLED: BrazeFeatureConfig = {
    ecommerceEnabled: false,
    guestCapture: false,
};

let brazeConfig: BrazeFeatureConfig | null = null;
let brazeConfigRequest: Promise<BrazeFeatureConfig> | null = null;

export const fetchBrazeFeatureConfig = async (): Promise<BrazeFeatureConfig> => {
    const response = await ApiHelper.get('/feature/braze');
    return {
        ecommerceEnabled: toBool(response?.braze_ecommerce_enabled),
        guestCapture: toBool(response?.braze_guest_capture),
    };
};

export const getBrazeFeatureConfig = async (): Promise<BrazeFeatureConfig> => {
    if (brazeConfig) return brazeConfig;
    if (!brazeConfigRequest) {
        brazeConfigRequest = fetchBrazeFeatureConfig()
            .then((config) => {
                brazeConfig = config;
                return config;
            })
            .catch((error) => {
                console.log('Braze Feature Config Error', error ? ` - ${error}` : '');
                return BRAZE_CONFIG_DISABLED;
            })
            .finally(() => {
                brazeConfigRequest = null;
            });
    }
    return brazeConfigRequest;
};

export const clearBrazeFeatureConfigCache = () => {
    brazeConfig = null;
};
